import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { createPortal } from "react-dom";

interface BasePageModalProps {
  title: string; // ヘッダーのタイトル
  className?: string; // モーダルのメインコンテンツ(sectionの中身)のクラス名
  handleClose: () => void; // 閉じる（キャンセル）ボタン押下時のイベント
  children?: React.JSX.Element | Array<React.JSX.Element>; // ボディ部
  bottomChildren?: React.JSX.Element | Array<React.JSX.Element>; // フッター部に追加する要素
  disableOverlayClose?: boolean; // オーバーレイクリックでモーダルを閉じることを無効化するかどうか
  isSub?: boolean; // サブモーダルかどうか（サブモーダルの場合はcreatePortalでbody直下にモーダルを描画する）
}

export const BasePageModal = (props: BasePageModalProps) => {
  const {
    title,
    className = "",
    children,
    bottomChildren,
    handleClose,
    disableOverlayClose = false,
    isSub = false,
  } = props;

  const [isClosing, setIsClosing] = useState(false);

  const { isLoading } = useContext(LoadingModalContext);
  const modalRef = useRef<HTMLElement>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab" || !modalRef.current) return;

    const focusableElements = Array.from(
      modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      if (["SECTION", "DIV", "ASIDE"].includes(el.tagName)) return false;
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
    if (focusableElements.length === 0) return;

    e.preventDefault();

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

    let nextIndex: number;
    if (currentIndex === -1) {
      // フォーカスがモーダル外またはsection等にある場合
      nextIndex = e.shiftKey ? focusableElements.length - 1 : 0;
    } else if (e.shiftKey) {
      nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
    }

    focusableElements[nextIndex].focus();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleOverlayClick = () => {
    if (!isLoading && !disableOverlayClose) {
      handleCloseClick();
    }
  };

  const handleCloseClick = () => {
    setIsClosing(true);

    setTimeout(() => {
      handleClose(); // 親に閉じてもらう
    }, 200); // ← CSSのtransitionと合わせる
  };

//  useEffect(() => {
//    // FIXME: 直接DOMを触らないようにref等を使うよう修正するべき
//
//    // NOTE: Safari(iOS)でモーダル時にスクロールを行ったときに画面下をちらつかせないようにする対応
//    if (isShow) {
//      // BasePageModalを開くときにスタイルを当てて画面下をちらつかせないようにする対応
//      document.body.style.position = "fixed";
//      document.body.style.overflow = "hidden";
//      document.body.style.inset = "0px";
//      document.documentElement.style.height = "100vh";
//    } else {
//      // BasePageModalを閉じるときにスタイルをデフォルト値に戻す
//      document.body.style.position = "static";
//      document.body.style.overflow = "visible";
//      document.body.style.inset = "auto";
//      document.documentElement.style.height = "100%";
//    }
//
//    return () => {
//      document.body.style.position = "static";
//      document.body.style.overflow = "visible";
//      document.body.style.inset = "auto";
//      document.documentElement.style.height = "100%";
//    };
//  }, [isShow]);

//  // NOTE: Safari(iOS)で中央にグレーの点を表示させない対応
//  const innerAreaOnTapWrapper = (e: React.MouseEvent) => {
//    if (innerAreaOnTap) innerAreaOnTap();
//    e.stopPropagation();
//  };

const modal = (
  // <AnimatePresence mode="wait">
  <>
    <div key="modalOverlay" id="modalOverlay" className={`overlay ${!isClosing ? "show" : ""}`} onClick={handleOverlayClick}></div>
    <aside
      ref={modalRef}
      key="modalAside"
      id="modal"
      className={`modal-container ${!isClosing ? "show" : ""}`}
    >
      <BaseRoundButton id="closeModal" className="btn-close" onClick={handleCloseClick} loadingNegative={true} />
      <div className="modal-content fade-in">
      {/*<div onClick={(e) => innerAreaOnTapWrapper(e)}>*/}
        <header>
          <h1>
            {title}
          </h1>
        </header>
        <section className={className}>
          {children}
        </section>
        {bottomChildren && (
        <nav>
          {bottomChildren}
        </nav>
        )}
      {/*</div>*/}
      </div>
    </aside>
  </>
  // </AnimatePresence>
);

  return (
    isSub ? createPortal(modal, document.body) : modal
  );
};
